import { create } from 'zustand';

import { Balance, ITonTransaction, TonChallenge } from '@/api/ton/types.ts';
import TonController from '@/api/ton/ton.ts';
import {
  TonChallengeStates,
  TonChallengeTypes,
  TonTransactionTypes
} from '@/enums/apiEnums.ts';
import { catchErrors } from '@/utils/sentry-monitor.tsx';

interface TonStore {
  balance?: Balance;
  challenges: { data: TonChallenge[]; next_page_token?: string };
  fetchBalance: () => Promise<void>;
  fetchChallenges: () => Promise<void>;
  replaceChallengeFromWS: (challenge: TonChallenge) => void;
  updateBalance: (transaction: ITonTransaction | undefined) => void;
  archiveChallenge: (challengeId: string) => void;
  hasActiveInviteFriendChallenge: () => boolean;
  getActiveChallengesCount: () => number;
  getChallengeByType: (
    challengeType: TonChallengeTypes
  ) => TonChallenge | undefined;
}

export const useTonStore = create<TonStore>((set, get) => ({
  balance: undefined,
  challenges: { data: [], next_page_token: undefined },
  fetchBalance: async () => {
    try {
      await new Promise((resolve) => setTimeout(resolve, 500));
      const balance = await TonController.getBalance();
      set({ balance });
    } catch (e) {
      catchErrors(e, true);
    }
  },

  fetchChallenges: async () => {
    try {
      await new Promise((resolve) => setTimeout(resolve, 500));
      const response = await TonController.getChallenges();
      set({
        challenges: {
          data: response.challenges,
          next_page_token: response.next_page_token
        }
      });
    } catch (e) {
      catchErrors(e, true);
    }
  },

  replaceChallengeFromWS: (challenge: TonChallenge) => {
    set((state) => ({
      challenges: {
        ...state.challenges,
        data: state.challenges.data.map((item) =>
          item.id === challenge.id ? challenge : item
        )
      }
    }));
  },

  updateBalance: (transaction: ITonTransaction | undefined) => {
    set((state) => {
      if (!state.balance || !transaction) {
        return state;
      }

      const transactionTonAmount = Number(transaction.ton_micros);
      const transactionUsdAmount = Number(transaction.usd_cents ?? 0);
      let newTonMicros = state.balance.ton_micros;
      let newUsdCents = state.balance.usd_cents;

      switch (transaction.type) {
        case TonTransactionTypes.TYPE_INCOME:
          newTonMicros += transactionTonAmount;
          newUsdCents += transactionUsdAmount;
          break;
        case TonTransactionTypes.TYPE_WITHDRAWAL:
          newTonMicros -= Math.abs(transactionTonAmount);
          newUsdCents -= Math.abs(transactionUsdAmount);
          break;
        case TonTransactionTypes.TYPE_UNSPECIFIED:
          return state;
      }

      return {
        ...state,
        balance: {
          ...state.balance,
          ton_micros: newTonMicros,
          usd_cents: newUsdCents
        }
      };
    });
  },

  archiveChallenge: (challengeId: string) => {
    set((state) => ({
      challenges: {
        ...state.challenges,
        data: state.challenges.data.map((challenge) =>
          challenge.id === challengeId
            ? { ...challenge, state: TonChallengeStates.STATE_ARCHIVED }
            : challenge
        )
      }
    }));
  },

  hasActiveInviteFriendChallenge: () => {
    return get().challenges.data.some(
      (challenge) =>
        challenge.type === TonChallengeTypes.TYPE_INVITE_FRIEND &&
        challenge.state === TonChallengeStates.STATE_IN_PROGRESS
    );
  },

  getActiveChallengesCount: () => {
    return get().challenges.data.filter(
      (challenge) => challenge.state === TonChallengeStates.STATE_IN_PROGRESS
    ).length;
  },

  getChallengeByType: (challengeType: TonChallengeTypes) => {
    return get().challenges.data.find(
      (challenge) => challenge.type === challengeType
    );
  }
}));
